/**
 * WHY THESE OPTIONS AND NOT THE OTHERS — the recommendation rationale, at the top of the Findings tab.
 *
 * =====================================================================================
 * SUCCINCT IS THE REQUIREMENT, NOT A STYLE PREFERENCE. THIS PANEL IS THE SHORTEST ON THE SITE.
 *
 * Every other panel here answers "what did we measure". This one answers "what did you choose and
 * why not the rest", which is the first thing a customer asks and the thing a seller has to be able
 * to say out loud on a call. That makes brevity a FUNCTIONAL requirement: a rationale a reader has
 * to study is one that does not get repeated back correctly, and this site already carries ~1,600
 * lines of FINDINGS.md, a 1,281-line UC7 evaluation and an 897-line remediation research document
 * for anyone who wants the derivation.
 *
 * So the shape is deliberate and must be preserved by anyone editing it:
 *
 *   1. THE VERDICT — two sentences. What was chosen, for which layer.
 *   2. THE THREE THINGS THAT MUST LAND — the framings without which the verdict misleads.
 *   3. ONE LINE PER OPTION — six options, one or two sentences each, in a table.
 *   4. EVERYTHING ELSE — behind a single expander, pointing at the documents of record.
 *
 * ⚠️ IF THIS PANEL GROWS INTO A RESTATEMENT OF FINDINGS.md IT HAS FAILED, even if every added
 * sentence is true. The failure mode is not inaccuracy; it is a rationale nobody reads.
 *
 * =====================================================================================
 * ⚠️ NOT ONE FIGURE ON THIS PANEL. THAT IS THE HARD RULE AND IT IS STATED ON SCREEN.
 *
 * Every number elsewhere on this site comes from a live API call or a build-time parser over a
 * committed report. This panel has NEITHER source — the facts it summarises live in markdown
 * documents that no parser reads — so the only way a number could appear here is by being typed,
 * and a hand-typed figure from this site has already become a wrong number in a customer's own
 * written analysis. So the rationale is written to need none: "faster in both regions" and "failed
 * on all three events" are the load-bearing claims, and neither requires a value. The figures are
 * in the documents named in the expander, where they can be checked against the run that produced
 * them.
 *
 * ⚠️ AND WHERE A FIGURE WOULD HAVE BEEN LOAD-BEARING, THE SENTENCE IS WRITTEN WITHOUT IT rather
 * than being dropped. The 7-digit ceiling is the clearest case: the arithmetic is what makes U4 the
 * binding question, and the string says the ceiling falls BELOW the stated daily volume without
 * naming either value. The Capacity tab computes the real per-pool ceilings from GET /v1/pools,
 * which is where a checkable number belongs.
 *
 * =====================================================================================
 * THE FOUR CROSS-CUTTING STRINGS ARE IMPORTED, NOT RESTATED.
 *
 * They live in src/lib/caveats.js with the rest of the live caveats, for the reason that file's
 * header gives. Three of them exist so this page cannot mislead by omission, and each is asserted
 * in the rendered DOM by scripts/verify-web-live.sh:
 *
 *   · UC7 was BUILT AND MEASURED and then not chosen — the distinction between evidence and a
 *     paper judgement, and it also carries that the outage was Aurora's rather than ours.
 *   · UC7's cost is MEASURED, UC4's is an UNMEASURED UNKNOWN — without this the page reads as
 *     though UC4 passed a test it has never taken.
 *   · R14 is the HINGE — without this the page reads as though measurement settled it.
 *   · The rules layer is NOT the binding problem — without this the page's own prominence
 *     misleads, because U4 outranks everything on it.
 *
 * ⚠️ THE FOURTH IS THE ONE MOST LIKELY TO BE CUT AS OFF-TOPIC. It is not off-topic: it is the
 * frame. A recommendation page whose most prominent section is the rules-layer choice implies the
 * rules-layer choice is THE decision, and it is not.
 * =====================================================================================
 */

import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Table from '@cloudscape-design/components/table';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import {
  UC7_BUILT_AND_MEASURED_THEN_NOT_CHOSEN,
  MEASURED_COST_VS_UNMEASURED_UNKNOWN,
  R14_IS_THE_HINGE,
  RULES_LAYER_IS_NOT_THE_BINDING_PROBLEM,
} from '../lib/caveats.js';

/**
 * ONE OR TWO SENTENCES PER OPTION. Resist adding a third.
 *
 * ⚠️ THE TWO CHOSEN OPTIONS COME FIRST AND THE REST FOLLOW IN THE ORDER THEY WERE RULED OUT, not
 * in numeric order. A reader scanning this table should reach the answer before the alternatives,
 * and UC7 sits immediately after the chosen pair because it is the only rejected option that was
 * built and measured — which is the fact most likely to be lost if it is buried among the options
 * that were rejected on paper.
 *
 * The `verdict` column is a short word, not a sentence, because it is what a scan picks up. The
 * `why` column carries the whole argument for that option and nothing else — no cross-references,
 * no hedging, and no figures.
 */
const OPTIONS = [
  {
    option: 'UC1 — DynamoDB high-water mark + Valkey buffer',
    layer: 'Issuance',
    verdict: 'CHOSEN',
    tone: 'green',
    why:
      'Chosen unconditionally. Uniqueness is STRUCTURAL rather than coordinated: each region owns a ' +
      'disjoint serial range, so there is no coordination, no replication and no cross-region call ' +
      'on the hot path. Zero duplicates across every serial issued under measurement, with the two ' +
      'regions’ serial windows verified OVERLAPPING — so the ranges were genuinely being exercised ' +
      'against each other rather than merely apart — and failover was drilled live.',
  },
  {
    option: 'UC4 — Aurora DSQL',
    layer: 'Rules',
    verdict: 'CHOSEN',
    tone: 'green',
    why:
      'Chosen for the rules layer. It is genuinely active-active, verified bidirectionally, and the ' +
      'linkage was confirmed to be real rather than assumed — peered clusters naming each other, ' +
      'with a witness region, status ACTIVE. The cost is stated rather than absorbed: no foreign ' +
      'keys, no triggers, no PL/pgSQL and no ON DELETE RESTRICT, so six integrity guarantees live ' +
      'permanently in application code with no path back down.',
  },
  {
    option: 'UC7 — Aurora PostgreSQL Global Database + write forwarding',
    layer: 'Rules',
    verdict: 'NOT CHOSEN — built and measured first',
    tone: 'red',
    why:
      'The closest call on the board, and the only rejected option that was BUILT AND MEASURED ' +
      'before being ruled out. It gains real database-enforced referential integrity, a familiar ' +
      'operational model, a server-side statement_timeout UC4 structurally cannot have — and it is ' +
      'FASTER than UC4 at the median in us-east-1. It loses on availability, and the losing facts ' +
      'are intrinsic rather than misconfigured.',
  },
  {
    option: 'UC2 — two independent Aurora PostgreSQL clusters',
    layer: 'Rules',
    verdict: 'NOT CHOSEN',
    tone: 'red',
    why:
      'Two clusters with no replication and no consistency mechanism between them, which means the ' +
      'two regions could resolve the same label request differently. There is no active-active ' +
      'story here at all.',
  },
  {
    option: 'UC6 — block-claim issuance + Aurora PostgreSQL',
    layer: 'Issuance + rules',
    verdict: 'DROPPED before build',
    tone: 'grey',
    why:
      'It invented a coordination problem UC1 does not have — a SERIALIZABLE cross-region epoch ' +
      'write, which write forwarding forbids outright — and then needed a specific database to ' +
      'solve it. Partition the range by region and the conflict disappears, which is exactly what ' +
      'UC1 already does. Nothing was left to build.',
  },
  {
    option: 'UC3 — DAX in front of the ledger',
    layer: 'Read path',
    verdict: 'REJECTED',
    tone: 'grey',
    why:
      'Zero DAX reads ever succeeded, so the hit rate is n/a and not "good" — a negative result, ' +
      'reported as one. It was never a candidate for the issuance path in any case: issuance is a ' +
      'WRITE, and a read cache cannot make a write correct.',
  },
];

export default function RationalePanel() {
  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Why these options and not the others, short enough to read before the detail. The derivation is in the documents named at the bottom."
          actions={<Badge color="blue">rationale</Badge>}
        >
          Why we chose what we chose
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* ------------------------------------------------------------------ 1. the verdict */}
        <Box variant="p" fontSize="heading-s" data-testid="rationale-verdict">
          <strong>
            UC1 for issuance and UC4 for the rules layer. UC7 was built, measured, and then not
            chosen; UC2, UC6 and UC3 were ruled out earlier and for different reasons.
          </strong>{' '}
          UC1 is not a close call — its uniqueness is structural rather than coordinated, so nothing
          about a region failure or a replication delay can produce a duplicate. The rules layer is
          the close call, and it turns on availability: UC7 is the better database and UC4 has the
          better availability posture, so the choice follows the requirement the customer named.
        </Box>

        {/*
          1a. THE SAME REASONING WITHOUT THE SHORTHAND, BECAUSE THE READER IS NOT ON THIS PROJECT.

          ⚠️ WHY THIS WAS ADDED. A count of the rendered text of this tab found R7, R14 and A-U1
          used with NO definition anywhere on the page, alongside ECONNREFUSED, TCP RST,
          statement_timeout and "acked write". The verdict above is readable — "UC7 is the better
          database and UC4 has the better availability posture" — and then the very next sentence
          reaches for R8 and A-U1. So the argument was sound and the vocabulary was ours.

          The audience is USPS and Accenture Federal Services: senior, technical, and NOT the people
          who ran these tests. An identifier they cannot resolve does not read as precision; it reads
          as something they are expected to already know, which is how a reader quietly stops
          following an argument.

          ⚠️ THIS DOES NOT REPLACE THE PRECISE VERSION, and the ordering matters. The R-numbers are
          the customer's own requirement labels and are how this maps back to their document, so they
          stay everywhere they already are. This block goes FIRST and defines them, so by the time a
          reader meets "failed R8 under A-U1" they know that R8 is the customer's zero-outage
          requirement and that A-U1 is our own reading of it, still unconfirmed.

          Deliberately no numbers here. Latency and outage figures belong to the tabs that measured
          them; repeating one in a plain-language summary is how a figure gets quoted without its
          window, which has already happened once on this project.
        */}
        <Box variant="p" data-testid="rationale-plain">
          <strong>The same reasoning in plain terms.</strong> Both options vend real barcodes today.
          The difference is what happens during a planned or unplanned event — a database software
          update, or losing a region.{' '}
          <strong>
            UC4 keeps serving rules in both regions at once, with no single region that everything
            depends on.
          </strong>{' '}
          UC7 has exactly one region that accepts writes, so a rules update made anywhere has to
          travel there — and when that region restarts, callers in BOTH regions briefly get errors on
          rules lookups. We measured that on the real system, and it happened on every event we
          drove, including the planned, rehearsed one that is documented as controlled.{' '}
          <strong>
            UC7 is genuinely the better database: faster lookups, and it enforces the rules&apos; own
            integrity constraints inside the database rather than in application code.
          </strong>{' '}
          We chose UC4 anyway, because the requirement the customer stated was about staying
          available.
        </Box>
        <Box variant="p" data-testid="rationale-glossary">
          <strong>The shorthand used below, in one place.</strong> <strong>R2, R7, R8, R14</strong>{' '}
          are the customer&apos;s own numbered requirements — R7 is &quot;accept rules writes in both
          regions&quot;, R8 is &quot;zero outages&quot;, and R14 is the customer&apos;s statement
          that rules are already enforced in their application today.{' '}
          <strong>A-U1 and A-U2 are OUR assumptions, not the customer&apos;s words</strong> — A-U1 is
          our reading of &quot;zero outages&quot; as meaning zero unavailability from the caller&apos;s
          point of view, which is the stricter of the two possible readings and is still unconfirmed.{' '}
          <strong>UC1, UC2, UC4, UC7</strong> are our labels for the candidate designs, one per tab.
          Aurora <strong>DSQL</strong> and Aurora <strong>PostgreSQL Global Database</strong> are the
          two AWS databases being compared for the rules layer. An <strong>acked write</strong> is
          one the database confirmed it had saved before the failure — none were lost by either
          option.
        </Box>

        {/*
          2. THE THREE THINGS THAT MUST LAND, AND THE FRAME.

          ⚠️ THESE ARE ALERTS RATHER THAN PROSE, AND NEVER BEHIND AN EXPANDER, for the same reason
          the live caveats below them are not: each one exists to stop a specific misreading of the
          verdict directly above, and a qualifier the reader must click for does not qualify
          anything. The order is deliberate — the frame LAST, because it is the widest claim and
          reads as a closing correction rather than as a preamble to be skipped.
        */}
        <Alert type="info" header="UC7 was built and measured before it was ruled out" data-testid="rationale-uc7">
          <Box variant="p">{UC7_BUILT_AND_MEASURED_THEN_NOT_CHOSEN}</Box>
        </Alert>

        <Alert
          type="warning"
          header="A measured cost and an unmeasured unknown are not the same claim"
          data-testid="rationale-asymmetry"
        >
          <Box variant="p">{MEASURED_COST_VS_UNMEASURED_UNKNOWN}</Box>
        </Alert>

        <Alert type="warning" header="One unanswered question moves this more than any measurement" data-testid="rationale-hinge">
          <Box variant="p">{R14_IS_THE_HINGE}</Box>
        </Alert>

        <Alert type="warning" header="And this is not the decision that binds you" data-testid="rationale-frame">
          <Box variant="p">{RULES_LAYER_IS_NOT_THE_BINDING_PROBLEM}</Box>
        </Alert>

        {/* -------------------------------------------------------------- 3. one line per option */}
        <Table
          variant="embedded"
          data-testid="rationale-options"
          header={
            <Header variant="h3" description="Each option in one or two sentences. Chosen first, then in the order they were ruled out.">
              Every option, and what decided it
            </Header>
          }
          items={OPTIONS}
          columnDefinitions={[
            {
              id: 'option',
              header: 'Option',
              cell: (i) => <strong>{i.option}</strong>,
              width: 300,
            },
            { id: 'layer', header: 'Layer', cell: (i) => i.layer, width: 130 },
            {
              id: 'verdict',
              header: 'Verdict',
              cell: (i) => <Badge color={i.tone}>{i.verdict}</Badge>,
              width: 220,
            },
            { id: 'why', header: 'What decided it', cell: (i) => i.why },
          ]}
        />

        {/*
          4. THE NO-FIGURES RULE, STATED IN THE OPEN AND NOT INSIDE THE EXPANDER BELOW IT.

          ⚠️ THIS SENTENCE WAS MOVED OUT OF THE EXPANDER DELIBERATELY, and the reason generalises:
          `document.body.innerText` — which is what scripts/verify-web-live.sh reads, because a
          snapshot can be stale — EXCLUDES the contents of a collapsed disclosure. So a rule stated
          only inside the expander is a rule no assertion can see, and the check would have had to
          click something to find it, which tests the script's ordering rather than the page. It also
          happens to be the right editorial call independently: the reader who needs to know this
          panel has no parenthetical figures is the reader skimming it, not the one already digging.
        */}
        <Box variant="small" color="text-body-secondary" data-testid="rationale-no-figures">
          This panel carries <strong>no figures at all</strong>. It has no live API and no build-time
          parser behind it — unlike every other figure on this site — so any number here could only be
          hand-typed, and a hand-typed figure from this site has already become a wrong number in a
          customer’s own written analysis. The measured values live in the documents below, beside the
          run that produced them.
        </Box>

        {/* ----------------------------------------------------------- 5. everything else, behind one control */}
        <ExpandableSection
          headerText="Where the derivation lives, and what each document settles"
          variant="footer"
        >
          <SpaceBetween size="s">
            <ul>
              <li>
                <Box variant="code">docs/FINDINGS.md</Box> — the recommendation of record, and the
                source of the recommendation text summarised in the section below this one.
              </li>
              <li>
                <Box variant="code">loadtest/results/UC7-EVALUATION.md</Box> — UC7 driven through a
                live engine patch, a managed switchover and an unplanned failover, with the
                caller-visible outage measured per region for each. Its §9 is the verdict this panel
                compresses.
              </li>
              <li>
                <Box variant="code">docs/greenfield/uc7-switchover-forensics.md</Box> — where the
                switchover outage went, attributed from Aurora’s own event stream and the driver’s
                own error code rather than inferred. This is the document that establishes the outage
                was Aurora refusing connections and not our client.
              </li>
              <li>
                <Box variant="code">docs/greenfield/uc7-flaw-remediation-research.md</Box> — whether
                each measured flaw can be engineered away. Two cannot, and those two are the ones
                that decide the recommendation.
              </li>
              <li>
                <Box variant="code">docs/greenfield/WHY-UC6-WAS-DROPPED.md</Box> — the finding that
                closed UC6 before it was built, kept because the reasoning outlives the design.
              </li>
            </ul>
            <Box variant="p" color="text-body-secondary">
              <strong>A-U1</strong> and <strong>A-U2</strong> are <em>our</em> internal assumptions
              pending the customer’s answers, never customer statements — A-U1 being the stricter
              reading of “zero outages”, chosen deliberately so that being told later a
              write-rejection window is acceptable is a cheap correction rather than a missed
              requirement. <strong>R7 is not met in letter</strong> by write forwarding: it gives a
              write <em>endpoint</em> per region, not a <em>writer</em> per region, and those are one
              word apart and a whole requirement apart.
            </Box>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Container>
  );
}
